import { hash128, ulid } from "@core/foundation/id";
import {
  createModel,
  createModelId,
  createModelSchemas,
  noModelUpdate,
} from "@core/foundation/model";
import { z } from "@core/zod";

import { ID_PREFIX } from "./shared/id-prefix.ts";

const {
  generateDeterministicId: generateOAuthClientId,
  idSchema: OAuthClientIdSchema,
} = createModelId({ digestFn: ulid, idPrefix: ID_PREFIX.OAUTH_CLIENT });

export { OAuthClientIdSchema };

export type OAuthClientId = z.infer<typeof OAuthClientIdSchema>;

export const OAUTH_GRANT_TYPES = [
  "authorization_code",
  "refresh_token",
] as const;

export const OAuthGrantTypeSchema = z.enum(OAUTH_GRANT_TYPES);

export type OAuthGrantType = z.infer<typeof OAuthGrantTypeSchema>;

export const OAuthClient = createModel({
  create: (item, schema) => {
    const decoded = schema.decode(item);

    const digest = hash128(
      JSON.stringify([
        decoded.clientName,
        decoded.grantTypes.toSorted(),
        decoded.redirectUris.toSorted(),
      ]),
    );

    return { ...decoded, id: generateOAuthClientId(digest) };
  },
  schemas: createModelSchemas({
    defaults: { grantTypes: OAUTH_GRANT_TYPES },
    model: z.object(
      {
        clientName: z.string(),
        grantTypes: z.array(OAuthGrantTypeSchema),
        id: OAuthClientIdSchema,
        redirectUris: z.array(z.url()),
      },
      {
        clientName: "The name the client registered under.",
        grantTypes: "The OAuth grant types the client may use.",
        id: "The client id, derived from the registration.",
        redirectUris: "The redirect URIs the client registered.",
      },
    ),
    modelCreate: ({ readSchema }) => readSchema.omit({ id: true }),
    modelUpdate: noModelUpdate,
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface OAuthClient extends z.infer<typeof OAuthClient.schema> {}
